/**
 * Shared card-slider runtime.
 * Wires prev/next arrow buttons to smooth-scroll a card track, for any block
 * following the `{root}__track` / `{root}__card` / `{root}__arrow--prev` /
 * `{root}__arrow--next` BEM convention (established by properties-featured).
 * Scrolls to the adjacent card element directly (rather than by a fixed pixel
 * amount) so it plays nicely with `scroll-snap-type` and varying card widths.
 *
 * On top of the block's own header arrows this adds two things modelled on
 * luxuryboltholes.com's "Featured properties" row:
 *   - pointer drag, so a mouse can scroll the track at any breakpoint rather
 *     than only paging via the arrows (touch already scrolls natively);
 *   - a second, injected pair of arrows overlaid on the track's left/right
 *     edges, fading in on hover. Injected here rather than added to each
 *     block's render.php so every consumer of this runtime gets them for free.
 *
 * `initCarousel` is the entry point for blocks following that BEM convention,
 * and binds at document-ready. `buildEdgeNav`, `enableDrag` and
 * `releaseVerticalWheel` are exported separately for tracks built at runtime,
 * which the ready-time sweep can't see -- the property-gallery lightbox's
 * thumbnail strip is the first of those. Using these rather than reimplementing
 * means a track gets identical drag physics and edge arrows wherever it lives.
 */
import $ from 'jquery';

// Past this many pixels of pointer travel the gesture counts as a drag rather
// than a click, and the click that follows pointerup is swallowed -- cards are
// links, and without this every drag that ends over a card navigates away.
const DRAG_THRESHOLD = 5;

export function initCarousel(root: string)
	{
	$(() =>
		{
		const $blocks = $(`.${root}`);
		if (!$blocks.length)
			return;

		$blocks.each((_i, block) =>
			{
			const $block = $(block);
			const $track = $block.find(`.${root}__track`);
			const $cards = $track.find(`.${root}__card`);
			const track = $track.get(0) as HTMLElement;
			if (!track || !$cards.length)
				return;

			// The injected edge arrows join the block's own header arrows in
			// the same jQuery sets, so click binding and disabled-state below
			// drive both pairs without knowing which is which.
			//
			// data-edge-nav="off" opts out: a block whose design puts the only
			// arrows in its header (the floor plans) would otherwise show two
			// pairs, one of them floating over the artwork.
			const $edge = $block.data('edge-nav') === 'off' ? $() : buildEdgeNav($track);
			const $prev = $block.find(`.${root}__arrow--prev`).add($edge.filter('.ocean-nav-edge__button--prev'));
			const $next = $block.find(`.${root}__arrow--next`).add($edge.filter('.ocean-nav-edge__button--next'));

			// The scroll offsets the track can actually come to rest at, in
			// ascending order.
			//
			// A card's start offset is its `offsetLeft` relative to the track,
			// but the last cards in a row can't be start-aligned: their offsets
			// run past `maxScroll`, since once the track is scrolled fully right
			// there's nothing left to pull them across. Clamping (rather than
			// taking the raw offsets) is what keeps the end of the track
			// reachable -- without it the nearest *unclamped* card to a track
			// scrolled hard right is the last fully-alignable one, several
			// hundred pixels back, and every drag to the end snaps back to it.
			//
			// Recomputed per call, never cached: card widths, the track's own
			// width and `maxScroll` all move with the viewport.
			const restPoints = () =>
				{
				const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
				// Seeded with 0, which is always a legitimate place to rest but
				// is NOT necessarily any card's offset: property-list leads its
				// track with a non-card gutter item (the bird), whose entire job
				// is to be visible at the start. Built from card offsets alone,
				// the lowest rest point was the FIRST CARD's -- so once you had
				// scrolled away, every settle snapped there and the gutter could
				// never be brought back. De-duplicated below, so tracks whose
				// first card already sits at 0 are unaffected.
				const points: number[] = [0];
				$cards.each((_i, card) =>
					{
					const point = Math.min((card as HTMLElement).offsetLeft - track.offsetLeft, maxScroll);
					// Every card past the alignable ones clamps to the same
					// value, so de-duplicate -- otherwise the arrows would need
					// several clicks to get off the final rest point.
					if (!points.length || point - points[points.length - 1] > 1)
						points.push(point);
					});
				// A card wider than the track would clamp short of the end;
				// guarantee the end is always in the list.
				if (!points.length || points[points.length - 1] < maxScroll - 1)
					points.push(maxScroll);
				return points;
				};

			const scrollTo = (left: number) =>
				{
				track.scrollTo({ left, behavior: 'smooth' });
				};

			// Nearest rest point to where the track currently sits -- what a
			// released drag should settle onto.
			const nearest = () =>
				{
				return restPoints().reduce((best, point) =>
					Math.abs(point - track.scrollLeft) < Math.abs(best - track.scrollLeft) ? point : best);
				};

			const updateArrowState = () =>
				{
				const points = restPoints();
				// Against the first and last rest points, not 0 and `maxScroll`:
				// a track with inline padding rests its first card at the
				// padding, not at 0, so testing `scrollLeft <= 0` would leave
				// prev enabled at the start forever.
				const atStart = track.scrollLeft <= points[0] + 1;
				const atEnd = track.scrollLeft >= points[points.length - 1] - 1;
				$prev.prop('disabled', atStart);
				$next.prop('disabled', atEnd);
				// Exhausted in both directions at once means every card already
				// fits -- there's nothing to page through, so the pair drops out
				// entirely rather than sitting there permanently greyed out. The
				// class goes on the block root, not the nav wrapper, so it works
				// whatever each block calls that wrapper (see scss/_nav-button.scss).
				$block.toggleClass('ocean-nav-static', atStart && atEnd);
				};

			// Stepping by rest point rather than by card index: at the right-hand
			// end several cards share one rest point, and index arithmetic there
			// yields clicks that don't move the track.
			$prev.on('click', () =>
				{
				const points = restPoints();
				const target = [...points].reverse().find(point => point < track.scrollLeft - 1);
				scrollTo(target ?? points[0]);
				});
			$next.on('click', () =>
				{
				const points = restPoints();
				const target = points.find(point => point > track.scrollLeft + 1);
				scrollTo(target ?? points[points.length - 1]);
				});

			$track.on('scroll', updateArrowState);
			// Whether the cards fit is width-dependent, so the state has to be
			// recomputed on resize -- without this a track that fits at one
			// width keeps its arrows hidden after the viewport narrows enough
			// to need them (and vice versa).
			$(window).on('resize', updateArrowState);
			updateArrowState();

			enableDrag(track, () => scrollTo(nearest()));
			releaseVerticalWheel(track);
			});
		});
	}

/**
 * Injects the hover-revealed edge arrow pair, as a sibling of the track inside
 * whatever element already contains it.
 *
 * Deliberately NOT wrapped around the track: the overlay needs a positioned
 * containing block, and introducing a new positioned ancestor *between* the
 * block root and the track would re-anchor anything inside the track that's
 * already absolutely positioned against that root (property-list's bird mark
 * on mobile, for one). Sharing the track's own parent and marking it with
 * `.ocean-nav-edge-host` leaves every existing containing block untouched.
 *
 * The overlay spans the track's box rather than the parent's -- the parent
 * usually also holds the block's header -- so its vertical extent is fed
 * through CSS custom properties measured here and kept current on resize.
 */
/**
 * The theme's arrow glyph, for buttons built at runtime.
 *
 * Kept here as markup rather than read off a nav button already in the page.
 * A page carrying a slider and nothing else -- Destinations, whose only
 * carousel is the destinations-slider -- has no rendered arrow to copy, so a
 * lookup returned '' and both injected buttons were built empty: correctly
 * sized, correctly coloured, and painting nothing.
 *
 * Source of truth is _shared/icons/arrow-right.svg, which NavButton.tsx
 * imports through SVGR. This module builds HTML strings rather than React, so
 * it can't use that component; the two must be changed together. `fill` stays
 * `currentColor` so the button's own colour drives it, and the viewBox has to
 * survive -- without it a CSS width/height crops the drawing instead of
 * scaling it (see the SVGO note in webpack.config.js).
 */
const NAV_ARROW_SVG =
	`<svg class="ocean-nav-button-icon" preserveAspectRatio="none" overflow="visible" ` +
		`style="display: block;" width="40" height="40" viewBox="0 0 40 40" fill="none" ` +
		`xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">` +
		`<path d="M34.6344 20.8844L23.3844 32.1344C23.1498 32.3689 22.8317 32.5007 22.5 32.5007C22.1683 32.5007 21.8502 32.3689 21.6156 32.1344C21.3811 31.8998 21.2493 31.5817 21.2493 31.25C21.2493 30.9183 21.3811 30.6002 21.6156 30.3656L30.7328 21.25H6.25C5.91848 21.25 5.60054 21.1183 5.36612 20.8839C5.1317 20.6495 5 20.3315 5 20C5 19.6685 5.1317 19.3505 5.36612 19.1161C5.60054 18.8817 5.91848 18.75 6.25 18.75H30.7328L21.6156 9.63438C21.3811 9.39982 21.2493 9.08171 21.2493 8.75C21.2493 8.4183 21.3811 8.10018 21.6156 7.86563C21.8502 7.63107 22.1683 7.49931 22.5 7.49931C22.8317 7.49931 23.1498 7.63107 23.3844 7.86563L34.6344 19.1156C34.7506 19.2317 34.8428 19.3696 34.9057 19.5213C34.9686 19.6731 35.001 19.8357 35.001 20C35.001 20.1643 34.9686 20.3269 34.9057 20.4787C34.8428 20.6304 34.7506 20.7683 34.6344 20.8844Z" fill="currentColor"/>` +
	`</svg>`;

export function navArrowMarkup(): string
	{
	return NAV_ARROW_SVG;
	}

export function buildEdgeNav($track: JQuery<HTMLElement>): JQuery<HTMLElement>
	{
	const track = $track.get(0) as HTMLElement;
	const host = track?.parentElement;
	if (!track || !host)
		return $();

	const icon = navArrowMarkup();

	// Unwrapped: the glyph is a complete <svg> that already carries the
	// .ocean-nav-button-icon class, so a <span> around it would apply that
	// class's sizing twice.
	const $nav = $(`<div class="ocean-nav-edge">` +
		`<button type="button" class="ocean-nav-edge__button ocean-nav-edge__button--prev ocean-nav-button ocean-nav-button--prev is-style-large" aria-label="Previous">` +
			icon +
		`</button>` +
		`<button type="button" class="ocean-nav-edge__button ocean-nav-edge__button--next ocean-nav-button ocean-nav-button--next is-style-large" aria-label="Next">` +
			icon +
		`</button>` +
	`</div>`);

	$(host).addClass('ocean-nav-edge-host');
	$nav.insertAfter($track);

	const measure = () =>
		{
		$nav.css({
			'--ocean-nav-edge-top': `${track.offsetTop}px`,
			'--ocean-nav-edge-height': `${track.offsetHeight}px`,
		});
		};
	$(window).on('resize', measure);
	// Card images are typically lazy and unsized until they land, which changes
	// the track's height after first measure -- re-measure whenever it does.
	if (typeof ResizeObserver !== 'undefined')
		new ResizeObserver(measure).observe(track);
	measure();

	// Each arrow answers only to its own side of the track, so the pointer has
	// to be near an edge to raise one -- not merely somewhere on the block.
	//
	// Bound on the host rather than the track: the buttons overlay the track
	// but aren't inside it, so tracking the track alone would fire a leave the
	// instant the pointer reached a button and flicker it out from under the
	// cursor. Geometry still comes from the track's own rect.
	const proximity = (e: JQuery.TriggeredEvent) =>
		{
		const ev = e.originalEvent as PointerEvent;
		if (ev?.pointerType === 'touch')
			return;
		const rect = track.getBoundingClientRect();
		// Proportional, so a narrow track doesn't end up with two zones that
		// meet in the middle, but capped so a full-bleed one doesn't put the
		// trigger a third of the way across the screen.
		const zone = Math.max(64, Math.min(160, rect.width * 0.12));
		const inBand = ev.clientY >= rect.top && ev.clientY <= rect.bottom;
		$nav
			.toggleClass('is-near-prev', inBand && ev.clientX - rect.left <= zone)
			.toggleClass('is-near-next', inBand && rect.right - ev.clientX <= zone);
		};

	$(host).on('pointermove', proximity);
	$(host).on('pointerleave', () =>
		{
		$nav.removeClass('is-near-prev is-near-next');
		});

	return $nav.find('.ocean-nav-edge__button');
	}

// A wheel gesture is treated as one continuous stream; this is how long a gap
// (ms) ends it. Comfortably longer than the ~16ms between events in a real
// swipe, including the tail of macOS momentum, but short enough that two
// deliberate swipes are never merged into one.
const GESTURE_GAP = 150;

// Lines and pages, converted to pixels for `deltaMode` 1 and 2. Chromium
// reports pixels for trackpads and wheels alike, but Firefox reports lines for
// a mouse wheel, and a raw `scrollBy` of "3" there would go nowhere.
const LINE_HEIGHT = 16;

/**
 * Stops the track swallowing vertical trackpad scrolling.
 *
 * A two-finger swipe is almost never perfectly vertical -- there's a few
 * pixels of `deltaX` in nearly every one. Browsers pick the element a wheel
 * gesture scrolls once, at its start, then *latch* to it for the rest of the
 * gesture: the stray horizontal component is enough for the track to claim the
 * swipe, after which the page is frozen until the fingers lift, however far
 * they then travel vertically. With a row of cards occupying most of the
 * viewport that reads as the page being stuck.
 *
 * So the gesture is classified on its first event and that verdict held for
 * its duration (diagonal drift mid-swipe mustn't flip modes):
 *   - `deltaX` of exactly 0 -- a mouse wheel, or a swipe the browser has
 *     already resolved as vertical. Left alone: the track can't scroll
 *     vertically, so chaining to the page already works, and the browser's own
 *     animated wheel scrolling is nicer than anything done by hand here.
 *   - horizontal-dominant -- the gesture means the track. Left alone too.
 *   - diagonal but vertical-dominant -- the case that latches. Taken off the
 *     track and applied to the page instead. Momentum events keep arriving
 *     after release and keep being forwarded, so the glide survives.
 */
export function releaseVerticalWheel(track: HTMLElement)
	{
	let until = 0;
	let steal = false;

	track.addEventListener('wheel', (e: WheelEvent) =>
		{
		const now = e.timeStamp;
		if (now > until)
			steal = e.deltaX !== 0 && Math.abs(e.deltaY) > Math.abs(e.deltaX);
		until = now + GESTURE_GAP;
		if (!steal)
			return;
		e.preventDefault();
		const scale = e.deltaMode === 1 ? LINE_HEIGHT : e.deltaMode === 2 ? window.innerHeight : 1;
		// `behavior: 'instant'` explicitly, against the document's own
		// `scroll-behavior: smooth`: a swipe arrives as dozens of events, and
		// each one inheriting smooth would start its own animation, every one
		// interrupting the last. The result trails the fingers badly. The
		// gesture is already smooth -- it just needs applying verbatim.
		window.scrollBy({ top: e.deltaY * scale, behavior: 'instant' });
		}, { passive: false });
	}

/**
 * Click-and-drag scrolling for pointers that can't swipe.
 *
 * Touch is left alone -- the track is `overflow-x: auto`, so touch already
 * scrolls it natively, and hijacking those events would only fight the
 * browser's own momentum and snapping.
 *
 * `settle` runs once a real drag ends. Restoring `scroll-snap-type` on its
 * own isn't enough: Chromium only re-snaps on the next scroll, so a released
 * drag would otherwise rest mid-card -- inconsistent with the `x mandatory`
 * the track declares and with how the same track behaves under touch.
 */
export function enableDrag(track: HTMLElement, settle: () => void)
	{
	const $track = $(track);
	let pointerId: number | null = null;
	let startX = 0;
	let startScroll = 0;
	let dragged = false;

	$track.on('pointerdown', (e) =>
		{
		const ev = e.originalEvent as PointerEvent;
		if (ev.pointerType === 'touch' || ev.button !== 0)
			return;
		pointerId = ev.pointerId;
		startX = ev.clientX;
		startScroll = track.scrollLeft;
		dragged = false;
		});

	$track.on('pointermove', (e) =>
		{
		const ev = e.originalEvent as PointerEvent;
		if (pointerId === null || ev.pointerId !== pointerId)
			return;
		const delta = startX - ev.clientX;
		if (!dragged)
			{
			if (Math.abs(delta) < DRAG_THRESHOLD)
				return;
			dragged = true;
			// Snapping and smooth-scroll both fight per-frame scrollLeft
			// writes; the class drops them for the duration (and restores
			// them on release, which gives the snap-back on let-go).
			$track.addClass('is-dragging');
			track.setPointerCapture(pointerId);
			}
		track.scrollLeft = startScroll + delta;
		});

	const end = () =>
		{
		if (pointerId === null)
			return;
		if (track.hasPointerCapture(pointerId))
			track.releasePointerCapture(pointerId);
		pointerId = null;
		const wasDrag = dragged;
		$track.removeClass('is-dragging');
		if (wasDrag)
			settle();
		};

	$track.on('pointerup pointercancel', end);

	// Capture phase, so the card's own link handler never sees the click that
	// terminates a drag. `dragged` is cleared here rather than in `end()` --
	// the click fires after pointerup.
	track.addEventListener('click', (e) =>
		{
		if (!dragged)
			return;
		dragged = false;
		e.preventDefault();
		e.stopPropagation();
		}, true);

	// Images and links are natively draggable, and a started HTML5 drag
	// cancels the pointer stream mid-gesture.
	$track.on('dragstart', (e) => { e.preventDefault(); });
	}
